import {Injectable, signal} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpResponse} from "@angular/common/http";
import {delay, map, Observable, of, throwError, switchMap} from "rxjs";
import {ResponseHttpSuccess, ResponseHttpError, LocalStorageService} from "library";
import {AuthService} from "projects/store/src/app/services/api/v1/auth.service";
import {environment} from "projects/store/src/environments/environment";
import {ActivatedRoute, Router} from "@angular/router";
import {RequestStatus} from "projects/store/src/app/enums/request-status.enum";
import {LocalStorageKey} from "projects/store/src/app/enums";

@Injectable({
  providedIn: 'root'
})
export class RequestService {
  url: string = environment.salesAPIURL;
  categoryId!: string;
  request: Record<string, any> = {};

  step = signal<number>(0);

  constructor(private http: HttpClient,
              private authService: AuthService,
              private localStorageService: LocalStorageService,
              private router: Router) {
  }

  updateStep(newStep: number) {
    this.step.set(newStep);
  }

  getSellerTraceability(storeId: string, queryString: string = ''): Observable<ResponseHttpSuccess> {
    return this.http.get(`${this.url}store/${storeId}/seller-traceability${queryString}`, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  getAll(): Observable<ResponseHttpSuccess> {

    // Llamada real a la API
    return this.http.get(`${this.url}user/me/requests/workable`, this.authService.getHttpOptionsBearer())
    .pipe(map((resp: any) => resp as ResponseHttpSuccess));
      // .pipe(
      //   map((resp: any) => {
      //     // Asegurarse de que la estructura de datos existe
      //     if (resp?.body?.data?.list && Array.isArray(resp.body.data.list)) {
      //       // Modificar la lista existente añadiendo sales_flow aleatoriamente
      //       interface SalesFlowOption {
      //         id: string;
      //         name: string;
      //         code: string;
      //       }

      //       interface ListItem {
      //         [key: string]: any;
      //         sales_flow?: SalesFlowOption;
      //       }

      //       const salesFlowOptions: SalesFlowOption[] = [
      //         { id: 'SF001', name: 'Flujo Domicilio', code: 'domicilio-cliente' },
      //         { id: 'SF002', name: 'Flujo Domicilio', code: 'domicilio-cliente' },
      //         { id: 'SF003', name: 'Flujo Domicilio', code: 'domicilio-vendedor' },
      //         { id: 'SF004', name: 'Flujo Domicilio', code: 'domicilio-vendedor' },
      //         { id: 'SF005', name: 'Flujo gestion tienda', code: 'gestion-tienda' },
      //         { id: 'SF006', name: 'Flujo general', code: 'general' }
      //       ];

      //       resp.body.data.list = (resp.body.data.list as ListItem[]).map((item: ListItem) => {
      //         // Añadir sales_flow con una probabilidad del 70%
      //         if (Math.random() < 0.7) {
      //           // Elegir un sales_flow aleatorio
      //           const randomFlowIndex: number = Math.floor(Math.random() * salesFlowOptions.length);
      //           return {
      //             ...item,
      //             sales_flow: salesFlowOptions[randomFlowIndex]
      //           };
      //         }
      //         return item;
      //       });
      //     }
      //     return resp as ResponseHttpSuccess;
      //   })
      // );
  }

  getSalesReport(month?: number, year?: number): Observable<ResponseHttpSuccess> {
    const hasPeriod = month !== undefined && year !== undefined;
    const options = {
      ...this.authService.getHttpOptionsBearer(),
      ...(hasPeriod ? {
        params: {
          month,
          year
        }
      } : {})
    };

    return this.http.get(`${this.url}additional-product/sales-report`, options)
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  get(id: string) {
    return this.http.get(`${this.url}request/${id}/workable`, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  update(id: string, data: any) {
    return this.http.patch(`${this.url}request/${id}/workable`, data, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  cancel(id: string, reason: string) {
    return this.update(id, {status: RequestStatus.CANCELADA, status_reason: reason})
  }

  approved(id: string) {
    return this.update(id, {status: RequestStatus.Aprobado})
  }

  getProductsByCategory(id: string, categoryId: string) {
    return this.http.get(`${this.url}request/${id}/category/${categoryId}/products`, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  getRequestAttributes(id: string, code: string): Observable<{ body: { data: any }, status: number }> {
    return this.http.get(`${this.url}request/${id}/attributes/${code}`, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  getAdditionalProductsAvailable(storeId: string, categoryId: string) {
    const options = {
      ...this.authService.getHttpOptionsBearer(),
      params: {
        store_id: storeId,
        category_id: categoryId
      }
    };

    return this.http.get(`${this.url}additional-product/available`, options)
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  saveInfoProductByRequest(id: string, data: any) {
    return this.http.post(`${this.url}request/${id}/product`, data, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  searchRequestByDocument(document: string){
    return this.http.get(`${this.url}request/workable/search?document_number=${document}`, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  getBrands(category_id: string){
    return this.http.get(`${this.url}brand?category_id=${category_id}`, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  getAgreement(id: string) {
    return this.http.get(`${this.url}request/${id}/agreement`, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  sendSingingAgreementCode(id: string, isRetry: boolean, otp_service: string): Observable<{ body: any, status: number }> {
    return this.http.post(`${this.url}request/${id}/agreement/notification/send-code`, {is_retry: isRetry, otp_service }, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  checkSingingAgreementCode(id: string, code: string): Observable<{ body: any, status: number }> {
    let data = {code};
    return this.http.post(`${this.url}request/${id}/agreement/check-code`, data, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  signAgreement(requestId: string){
    const data = {};
    return this.http.post(`${this.url}request/${requestId}/sign-agreement`, data, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  advancedSignAgreement(requestId: string){
    const data = {};
    return this.http.post(`${this.url}request/${requestId}/advanced-sign-agreement`, data, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  getCreditBureau(id: string) {
    return this.http.get(`${this.url}request/${id}/credit-bureau`, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  getCreditBureauDocument() {
    return this.http.get(`${this.url}country/setting/credit-bureau`, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  registerCreditBureauConsent(id: string, data: any) {
    return this.http.post(`${this.url}request/${id}/credit-bureau/consents`, data, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  sendCreditBureauCode(id: string, isRetry: boolean, otp_service: string){
    return this.http.post(`${this.url}request/${id}/credit-bureau/notification/send-code`, {is_retry: isRetry, otp_service}, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  checkCreditBureauCode(id: string, code: string) {
    let data = {code};
    return this.http.post(`${this.url}request/${id}/credit-bureau/check-code`, data, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  sendEmailVerificationCode(id: string, email: string, isRetry: boolean): Observable<{ body: any, status: number }> {
    return this.http.post(`${this.url}request/${id}/email/notification/send-code`, {email, is_retry: isRetry}, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  checkEmailVerificationCode(id: string, code: string): Observable<{ body: any, status: number }> {
    let data = {code};
    return this.http.post(`${this.url}request/${id}/email/check-code`, data, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  getCode(id: string, data: any): Observable<{ body: { data: any}, status: number }>{

    return this.http.post(`${this.url}request/${id}/opt-sms/code`, data, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  unlockSim(requestId: string){

    const data = {};
    return this.http.post(`${this.url}request/${requestId}/phone/unlock-sim`, data, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));

  }

  getEnrollmentStatus(id: string) {
    return this.http.get(`${this.url}request/${id}/enrollment`, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  createEnrollmentEngine(id: string, data: { imei: string }) {
    return this.http.post(`${this.url}request/${id}/enrollment`, data, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  getEnrollmentData(id: string) {
    return this.http.get(`${this.url}request/${id}/enrollment/info`, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  getEnrollmentEngineResult(requestId: string, enrollmentId: string) {
    return this.http.get(`${this.url}request/${requestId}/enrollment/${enrollmentId}`, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  getClientVerificationStart(id: string, callbackUrl: string) {
    let data = { callback_url: callbackUrl };

    return this.http.post(`${this.url}request/${id}/client-verification/start`, data, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  getClientVerificationStatus(id: string, data: any) {
    return this.http.post(`${this.url}request/${id}/client-verification/status`, data, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  getClientFacematchSettings(id: string) {
    return this.http.get(`${this.url}request/${id}/client-verification/facematch/setting`, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  getClientFacematchStatus(id: string, data: any) {
    return this.http.post(`${this.url}request/${id}/client-verification/facematch/status`, data, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  doLukiCashIn(id: string, data: any) {
    return this.http.post(`${this.url}request/${id}/luki/cash-in`, data, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  registerUserOnLuki(id: string) {
    //TODO: metodo que registra un usuario nuevo en luki, no estable, estan en conversaciones Javi con la gente de Luki por errorres


    const code = 204;
    switch (code) {
      /*case 200: return of({status: 200, body: { data: { list: [
              { id: 'rs345rt45553', nombre: 'A Y E CELULARES', monto_recarga_cw: 25,  monto_pagos_tienda: 520, monto_pagos_aliado: 800},
              { id: 'dfg5677997fg', nombre: 'ALBROOK 1', monto_recarga_cw: 15, monto_pagos_tienda: 680, monto_pagos_aliado: 600 },
              { id: 'rtgbn78907467', nombre: 'ALMACÉN AGROPECUARIO AZUERO', monto_recarga_cw: 30 , monto_pagos_tienda: 300, monto_pagos_aliado: 700},
              { id: 'rtbnd78907467', nombre: 'AIDY SHOP', monto_recarga_cw: 6, monto_pagos_tienda: 500, monto_pagos_aliado: 380 },
              { id: 'rtgbn789fg667', nombre: 'AUDIO MAX', monto_recarga_cw: 30, monto_pagos_tienda: 700, monto_pagos_aliado: 129 },
            ], totalRecords: 5}}});*/
      case 204: return of({status: 204, body: { data: {retries: 2}}});
      // case 401: return throwError({status: 401, message: 'Solicitud no terminada', body: {}});
      // case 406: return throwError({status: 406, message: 'Solicitud no terminada', body: { data: {reintentos: 2}}});
      default: return of({status: 0, body: { data: {retries: 2}}});
    }

    /*return this.http.post(`${this.url}request/${id}/luki/register`, {}, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));*/

  }

  saveBankAccountPaymentInformation(id: string, data: any) {
    return this.http.post(`${this.url}request/${id}/payment-information/bank-account`, data, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  getClientLukiAppStatus(id: string) {
    return this.http.get(`${this.url}request/${id}/luki-app-status`, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  create(data: any) {
    return this.http.post(`${this.url}request`, data, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  createAppKrdyaRequest(data: {kyc_last_session_id: string, telefono: string}) {
    return this.http.post(`${this.url}request/app-krdya`, data, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  retryImeiLogin(imei: any, id: any) {
    return this.http.post(`${this.url}request/${id}/enrollment/start`, imei, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  getEnrollmentStatusTelephone(id: any){
    return this.http.get(`${this.url}request/${id}/enrollment/status`, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  saveAdditionalData(id: string, data: {id: string, value: string}) {
    return this.http.put(`${this.url}request/${id}/additional-data`, data, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  getAdditionalData(id: string) {
    return this.http.get(`${this.url}request/${id}/additional-data`, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  getPreLoadData(documentNumber: string) {
    return this.http.get(`${this.url}pre-load-data/client/search?documentNumber=${documentNumber}`, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  getCategories() {
    return this.http.get(`${this.url}category`, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  getCategoriesList(id: any): Observable<{ body: { data: any}, status: number }> {
    return this.http.get(`${this.url}request/${id}/categories`, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  getBrandList(categoryId: string){
    return this.http.get(`${this.url}brand?category_id=${categoryId}`, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));

  }

  getStoresList(id: any): Observable<{ body: { data: any}, status: number }> {
    return this.http.get(`${this.url}request/${id}/stores`, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  getTopUpLoan(id: any): Observable<{ body: { data: any}, status: number }> {
    return this.http.get(`${this.url}request/${id}/top_up_loan`, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  getTopUpExpress(id: any) {
   return this.http.get(`${this.url}request/${id}/top_up_loan/express`, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  getCalculationCreditInstallments(data: any, requestId: string): Observable<{ body: { data: any}, status: number }> {
    return this.http.post(`${this.url}request/${requestId}/quotes/quotas/calcule`, data, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  getUserPhoneExist(phone: any, requestID: string, fieldKey?: string){
    if (requestID !== undefined && requestID !== null ) {
      return this.http.get(`${this.url}user/phone-exist/${phone}?solicitud_id=${requestID}&llave=${fieldKey}`, this.authService.getHttpOptionsBearer())
        .pipe(map((resp: any) => resp as ResponseHttpSuccess));
    }

    return this.http.get(`${this.url}user/phone-exist/${phone}`, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  getUserPhoneVerification(phone: any): Observable<{ body: { data: any }, status: number }> {
    return this.http.get(`${this.url}user/phone-verification/${phone}`, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  getUserEmailExist(email: any) {
    return this.http.get(`${this.url}user/email-exist/${email}`, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));

  }

  getValidStatusEmail(email: any){
    return this.http.get(`${this.url}user/email-verification/${email}`, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  sendMessageOtp(requestID: string, data: any){
    return this.http.post<ResponseHttpSuccess>(`${this.url}request/${requestID}/opt-sms/send`, data , this.authService.getHttpOptionsBearer())
      .pipe( map( resp => resp));
  }

  checkCode(requestID: string, code: any){
    return this.http.post<ResponseHttpSuccess>(`${this.url}request/${requestID}/opt-sms/check`, code,  this.authService.getHttpOptionsBearer())
      .pipe( map( resp => resp));
  }

  validateSimulation(requestId: string, data: any) {
    return this.http.post(`${this.url}request/${requestId}/validate-simulation`, data, this.authService.getHttpOptionsBearer())
      .pipe( map((resp: any) => resp as ResponseHttpSuccess) )
  }

  checkGeolocation(id: any,data:any): Observable<{ body: { data: any}, status: number }> {
    return this.http.post(`${this.url}request/${id}/validate-geolocalization`, data, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  getAppInformation(requestId: string){
    return this.http.get(`${this.url}request/${requestId}/app/status`, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));

  }

  sendSmsForSale(phone: string,  is_retry: boolean,  type: string, content: string){
    const completedPhone = environment.global.country.phoneCountryCode + phone;
    const data = {
      phone: completedPhone,
      is_retry: is_retry,
      type: type,
      content
    }

    return this.http.post(`${this.url}opt-sms/send`, data, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  getTinyUrl(original_url: string){
    const data = {original_url};

    return this.http.post(`${this.url}tiny-url`, data, this.authService.getHttpOptionsBearer())
      .pipe( map((resp: any) => resp as ResponseHttpSuccess) );

  }

  getInformationByCategoryStepId(id: string){
    return this.http.get(`${this.url}category/step/${id}`, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));

  }

  createCashDeposit(requestId: string){
    const data = {};

    return this.http.post(`${this.url}request/${requestId}/point-of-sales/bonus`, data, this.authService.getHttpOptionsBearer())
      .pipe( map((resp: any) => resp as ResponseHttpSuccess) );

  }

  generateOtpPointOfSales(id: string, cancelar: boolean = false) : Observable<any> {
    const body = cancelar ? { cancelar: true } : {};
    return this.http.post(`${this.url}request/${id}/point-of-sales/otp`, body, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess) );
  }

  getStatusOtpPointOfSales(id: string) : Observable<any>{
    return this.http.get(`${this.url}request/${id}/point-of-sales/otp/status`, this.authService.getHttpOptionsBearer())
      .pipe(map(resp => resp as ResponseHttpSuccess));
  }

  reGenerateOtpPointOfSales(id: string) : Observable<any>{
    return this.http.post(`${this.url}request/${id}/point-of-sales/otp/regenerate`, {}, this.authService.getHttpOptionsBearer())
      .pipe(map(resp => resp as ResponseHttpSuccess));
  }

  saveIdentityDocumentPhoto(id: string, body: { image: string }) {
    return this.http.put(`${this.url}request/${id}/identity-document-photo`, body, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  validateCategoryRequestByCreditExpress(categoryId: string, data: { document_id_number: string, birth_date: string }) {
    return this.http.post(`${this.url}category/${categoryId}/request/express`, data, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  searchRequest(documentNumber: string, salesFlowId: string){
    return this.http.get(`${this.url}request/workable/search?document_number=${documentNumber}&sales_flow_id=${salesFlowId}`, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }

  saveAttributes(id: string, code: string, data: any) {
    return this.http.post(`${this.url}request/${id}/attributes/${code}`, data, this.authService.getHttpOptionsBearer())
      .pipe(map((resp: any) => resp as ResponseHttpSuccess));
  }
}
